import type { EmbryoPolygenicRisk } from "@src/packages/cycles/hooks/useEmbryoReport";

/** Plot only an exact reported percentage; ranges and prose retain their text. */
export function readRiskPercentage(value: string): number | null {
  const match = value.trim().match(/^(\d+(?:[.,]\d+)?)\s*%$/);
  const digits = match?.[1];
  if (!digits) return null;
  const percentage = Number(digits.replace(",", "."));
  return Number.isFinite(percentage) && percentage >= 0 && percentage <= 100
    ? percentage
    : null;
}

/** Relative difference from the reported population risk, not percentage points. */
export function relativeRiskDifferencePercentage(
  risk: Pick<EmbryoPolygenicRisk, "absoluteRisk" | "populationRisk">,
): number | null {
  const absolute = readRiskPercentage(risk.absoluteRisk);
  const population = readRiskPercentage(risk.populationRisk);
  if (absolute === null || population === null) return null;
  if (absolute === population) return 0;
  if (population === 0) return null;
  const difference = ((absolute - population) / population) * 100;
  return Number.isFinite(difference) ? difference : null;
}

/** Every embryo and its own population reference share one zero-based scale. */
export function polygenicRiskScaleMaximum(
  risks: readonly Pick<
    EmbryoPolygenicRisk,
    "absoluteRisk" | "populationRisk"
  >[],
): number {
  const highest = Math.max(
    0,
    ...risks.flatMap((risk) =>
      [risk.absoluteRisk, risk.populationRisk]
        .map(readRiskPercentage)
        .filter((value): value is number => value !== null),
    ),
  );
  if (highest === 0) return 1;
  const magnitude = 10 ** Math.floor(Math.log10(highest));
  const step = [1, 2, 5, 10].find(
    (candidate) => candidate * magnitude >= highest,
  );
  return Math.min(100, (step ?? 10) * magnitude);
}

export function readRiskPercentile(percentile: number): number | null {
  return Number.isFinite(percentile) && percentile >= 0 && percentile <= 100
    ? percentile
    : null;
}

/** Fixed embryo columns; reported values share a vertical scale, including ties. */
export function layoutRiskColumns(
  values: readonly (number | null)[],
  maximum: number,
  width: number,
) {
  if (
    !Number.isFinite(maximum) ||
    maximum <= 0 ||
    !Number.isFinite(width) ||
    width < 48
  ) {
    return [];
  }
  const columnWidth = (width - 48) / values.length;
  return values.flatMap((value, index) => {
    if (
      value === null ||
      !Number.isFinite(value) ||
      value < 0 ||
      value > maximum
    ) {
      return [];
    }
    return [
      {
        index,
        value,
        x: 40 + (index + 0.5) * columnWidth,
        fraction: value / maximum,
      },
    ];
  });
}
